// Third party libs/components
import React, { useState, useEffect, useCallback } from 'react';
import P from 'prop-types';
import Style from './style.module.css';
import * as Styled from './ProfileForm.styles';
import { useForm, Controller } from 'react-hook-form';
import TextMaskInput from '../FormInputs/TextMaskInput';
import { isValidPhoneNumber } from 'react-phone-number-input';

// Components
import Select from '../FormInputs/Select';
import SpinningWheel from '../SpinningWheel';
import Dialog from '../Dialog';
import AccountInfo from '../AccountInfo';
import PhoneInput from '@components/FormInputs/PhoneInput';

// Utils
import { translate as t, useCurrentLang } from '@utils/translate';
import { getCountries } from '@utils/country';

// Services
import { fetchDocumentType } from '@services/user';
import { getUser } from '@services/auth';
import { useLayoutEffect } from 'react';

let birthdayDate = '';

const ProfileForm = ({
  onSubmit,
  onChange,
  loading,
  activeSubmit,
  errorMsg,
  hasDelete,
}) => {
  const TEXTS = {
    FULL_NAME: t('fullName'),
    FIRST_NAME: t('firstName'),
    LAST_NAME: t('lastName'),
    INVALID_NAME: t('invalidName'),
    INPUT_EMAIL: t('email'),
    INPUT_BIRTH_COUNTRY: t('birthCountry'),
    INVALID_EMAIL: t('invalidEmail'),
    INPUT_CPF: 'CPF',
    INVALID_CPF: t('invalidCpf'),
    INPUT_BIRTH: t('birthInput'),
    INVALID_BIRTH: t('invalidBirth'),
    REQUIRED_FIELD: t('requiredField'),
    INPUT_PHONE: t('phoneInput'),
    INVALID_PHONE: t('invalidPhone'),
    EMERGENCY_PHONE: t('emergencyPhone'),
    INPUT_DOCUMENT: t('documentInput'),
    INPUT_DOCUMENT_TYPE: t('documentTypeInput'),
    SAVE_BUTTON: t('saveButton'),
    DELETE_ACCOUNT: t('deleteAccount'),
  };

  const schema = {
    firstName: {
      id: 'firstName',
      name: 'firstName',
      type: 'text',
      label: TEXTS.FIRST_NAME,
      rules: {
        required: { value: true, message: TEXTS.REQUIRED_FIELD },
        validate: value => {
          const regex = /^[a-zA-ZÀ-ÿ\s]+$/g;
          if (!value.match(regex)) {
            return TEXTS.INVALID_NAME;
          }
          return true;
        },
      },
    },
    lastName: {
      id: 'lastName',
      name: 'lastName',
      type: 'text',
      label: TEXTS.LAST_NAME,
      rules: {
        required: { value: true, message: TEXTS.REQUIRED_FIELD },
        validate: value => {
          const regex = /^[a-zA-ZÀ-ÿ\s]+$/g;
          if (!value.match(regex)) {
            return TEXTS.INVALID_NAME;
          }
          return true;
        },
      },
    },
    email: {
      id: 'email',
      name: 'emailAddress',
      type: 'email',
      label: TEXTS.INPUT_EMAIL,
      rules: {
        pattern: {
          value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,4}$/i,
          message: TEXTS.INVALID_EMAIL,
        },
      },
    },
    cpf: {
      id: 'cpf',
      name: 'cpf',
      type: 'text',
      label: TEXTS.INPUT_CPF,
      mask: '999.999.999-99',
      maskChar: null,
      alwaysShowMask: false,
      rules: {
        required: { value: true, message: TEXTS.REQUIRED_FIELD },
        pattern: {
          value: /^[0-9]{3}\.[0-9]{3}\.[0-9]{3}\-[0-9]{2}$/i,
          message: TEXTS.INVALID_CPF,
        },
      },
    },
    date_birth: {
      id: 'date_birth',
      name: 'birthdayDate',
      type: 'text',
      label: TEXTS.INPUT_BIRTH,
      mask: '99/99/9999',
      maskChar: null,
      alwaysShowMask: false,
      rules: {
        required: { value: true, message: TEXTS.REQUIRED_FIELD },
        pattern: {
          value: /^[0-9]{2}\/[0-9]{2}\/[0-9]{2,4}$/i,
          message: TEXTS.INVALID_BIRTH,
        },
      },
    },
    phone: {
      id: 'phone',
      name: 'mobileNumber',
      type: 'text',
      label: TEXTS.INPUT_PHONE,
      placeholder: '+1 202 555 0156',
      rules: {
        required: { value: true, message: TEXTS.REQUIRED_FIELD },
        pattern: {
          value: /^\+[0-9 \-]+$/i,
          message: TEXTS.INVALID_PHONE,
        },
        validate: value => {
          const isValid = isValidPhoneNumber(value?.length ? value : '');
          if (!isValid) {
            return TEXTS.INVALID_PHONE;
          }
          return true;
        },
      },
    },
    emergency_phone: {
      id: 'emergency_phone',
      name: 'emergencyPhoneNumber',
      type: 'text',
      label: TEXTS.EMERGENCY_PHONE,
      placeholder: '+1 202 555 0156',
      rules: {
        pattern: {
          value: /|^\+[0-9 \-]+$/i,
          message: TEXTS.INVALID_PHONE,
        },
        validate: value => {
          if (!value) return true;
          const isValid = isValidPhoneNumber(value);
          if (!isValid) {
            return TEXTS.INVALID_PHONE;
          }
          return true;
        },
      },
    },
    documentType: {
      id: 'document-type',
      name: 'documentTypeId',
      type: 'text',
      label: TEXTS.INPUT_DOCUMENT_TYPE,
      rules: {
        required: { value: true, message: TEXTS.REQUIRED_FIELD },
      },
    },
    birthCountry: {
      id: 'birth-country',
      name: 'birthCountry',
      type: 'text',
      label: TEXTS.INPUT_BIRTH_COUNTRY,
      rules: {
        required: { value: true, message: TEXTS.REQUIRED_FIELD },
      },
    },
    document: {
      id: 'document',
      name: 'identityDocument',
      type: 'text',
      label: TEXTS.INPUT_DOCUMENT,
      rules: {
        required: { value: true, message: TEXTS.REQUIRED_FIELD },
      },
    },
  };

  const { errors, handleSubmit, control, register } = useForm();
  const lang = useCurrentLang();
  const data = getUser();

  // Birth Country
  const [birthCountry, setBirthCountry] = useState(
    data.countryOfBirth ? data.countryOfBirth.iso2Code : ''
  );
  const [countryList, setCountryList] = useState([]);
  const [dialogOpen, setDialogOpen] = useState();
  // Document List
  const [documentList, setDocumentList] = useState([]);
  const [documentType, setDocumentType] = useState(data.documentTypeId);

  if (data.birthdayDate) {
    const parts = data.birthdayDate.split('-');
    birthdayDate = parts[2] + '/' + parts[1] + '/' + parts[0];
  }

  useEffect(() => {
    fetchDocumentType().then(({ data: resData }) => {
      setDocumentList(resData.documentTypes);
    });
  }, []);

  useEffect(() => {
    const countryLang = lang.split('_')[0] || 'pt';
    setCountryList(getCountries(countryLang));
  }, [lang]);

  const handleChangeDocumentType = useCallback(evt => {
    setDocumentType(evt.target.value);
  }, []);

  const handleBirthCountry = useCallback(evt => {
    setBirthCountry(evt.target.value);
  }, []);

  return (
    <Styled.Form onSubmit={handleSubmit(onSubmit)} onChange={onChange}>
      <Styled.FlexContainer>
        <Styled.LeftContainer>
          <Styled.FormRow>
            <Styled.InputContainer>
              <Styled.LabelInput htmlFor={schema.firstName.id}>
                {schema.firstName.label}
              </Styled.LabelInput>
              <Controller
                as={TextMaskInput}
                control={control}
                defaultValue={data.firstName ? data.firstName : ''}
                {...schema.firstName}
                errors={errors}
                className={Style.input}
              />
            </Styled.InputContainer>
            <Styled.InputContainer>
              <Styled.LabelInput htmlFor={schema.lastName.id}>
                {schema.lastName.label}
              </Styled.LabelInput>
              <Controller
                as={TextMaskInput}
                control={control}
                defaultValue={data.lastName ? data.lastName : ''}
                {...schema.lastName}
                errors={errors}
                className={Style.input}
              />
            </Styled.InputContainer>
          </Styled.FormRow>

          <Styled.FormRow>
            <Styled.InputContainer>
              <Styled.LabelInput htmlFor={schema.email.id}>
                {schema.email.label}
              </Styled.LabelInput>
              <Controller
                as={TextMaskInput}
                control={control}
                defaultValue={data.emailAddress ? data.emailAddress : ''}
                {...schema.email}
                errors={errors}
                className={Style.input}
              />
            </Styled.InputContainer>

            <Styled.InputContainer>
              <Styled.LabelInput htmlFor={schema.birthCountry.id}>
                {schema.birthCountry.label}
              </Styled.LabelInput>
              <Select
                attrLabel="name"
                attrValue="code"
                register={register}
                options={countryList}
                value={birthCountry}
                defaultOption={' '}
                errors={errors}
                className={Style.input}
                onChange={handleBirthCountry}
                {...schema.birthCountry}
              />
            </Styled.InputContainer>
          </Styled.FormRow>

          <Styled.FormRow>
            <Styled.InputContainer>
              <Styled.LabelInput htmlFor={schema.documentType.id}>
                {schema.documentType.label}
              </Styled.LabelInput>

              <Select
                attrLabel="name"
                attrValue="id"
                register={register}
                options={documentList}
                value={documentType || ''}
                errors={errors}
                className={Style.input}
                onChange={handleChangeDocumentType}
                {...schema.documentType}
              />
            </Styled.InputContainer>

            <Styled.InputContainer>
              <Styled.LabelInput htmlFor={schema.document.id}>
                {schema.document.label}
              </Styled.LabelInput>
              <Controller
                as={TextMaskInput}
                control={control}
                defaultValue={
                  data.identityDocument ? data.identityDocument : ''
                }
                {...schema.document}
                errors={errors}
                className={Style.input}
              />
            </Styled.InputContainer>
          </Styled.FormRow>
        </Styled.LeftContainer>

        <Styled.RightContainer>
          <Styled.InputContainer>
            <Styled.LabelInput htmlFor={schema.date_birth.id}>
              {schema.date_birth.label}
            </Styled.LabelInput>
            <Controller
              as={TextMaskInput}
              control={control}
              defaultValue={birthdayDate}
              {...schema.date_birth}
              errors={errors}
              className={Style.input}
            />
          </Styled.InputContainer>

          <Styled.InputContainer>
            <Styled.LabelInput htmlFor={schema.phone.id}>
              {schema.phone.label}
            </Styled.LabelInput>
            <Controller
              as={PhoneInput}
              control={control}
              defaultValue={data.mobileNumber ? data.mobileNumber : ''}
              {...schema.phone}
              errors={errors}
              className={Style.input}
            />
          </Styled.InputContainer>

          <Styled.InputContainer>
            <Styled.LabelInput htmlFor={schema.emergency_phone.id}>
              {schema.emergency_phone.label}
            </Styled.LabelInput>
            <Controller
              as={PhoneInput}
              control={control}
              defaultValue={
                data.emergencyPhoneNumber ? data.emergencyPhoneNumber : ''
              }
              {...schema.emergency_phone}
              errors={errors}
              className={Style.input}
            />
          </Styled.InputContainer>
        </Styled.RightContainer>
      </Styled.FlexContainer>
      <Styled.FlexContainer>
        <Styled.Button
          type="submit"
          disabled={!activeSubmit}
          style={{ marginRight: 10 }}
        >
          {TEXTS.SAVE_BUTTON}
          {loading && (
            <div className="loading">
              <SpinningWheel />
            </div>
          )}
        </Styled.Button>
        {hasDelete && (
          <Styled.Button
            type="button"
            color="transparent"
            style={{ color: '#707070' }}
            onClick={() => setDialogOpen(!dialogOpen)}
          >
            {TEXTS.DELETE_ACCOUNT}
          </Styled.Button>
        )}
      </Styled.FlexContainer>

      <Dialog open={dialogOpen} setOpen={setDialogOpen}>
        <AccountInfo
          open={dialogOpen}
          setOpen={setDialogOpen}
          genericData={data}
          documentList={documentList}
        />
      </Dialog>

      <p className={Style.error}> {errorMsg} </p>
    </Styled.Form>
  );
};

ProfileForm.propTypes = {
  data: P.object,
  onSubmit: P.func,
  onChange: P.func,
  loading: P.bool,
  activeSubmit: P.bool,
  errorMsg: P.string,
  hasDelete: P.bool,
};

export default ProfileForm;
